import { useState } from 'react'
import { format, subDays } from 'date-fns'
import { parseAsString, useQueryStates } from 'nuqs'

import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { Card, CardContent } from '@/common/ui/card'
import { PathsApiAdminMessageAnalyticsGetParametersQueryMessageType } from '@/schema'

import useQueryGetMessagingAnalytics from '../../api/hooks/useQueryGetMessagingAnalytics'
import MessagingAnalyticsFilters from '../../components/MessagingAnalyticsFilters'
import ReactivationOverviewCards from '../../components/ReactivationOverviewCards'
import CategoryConversionTable from '../../components/CategoryConversionTable'
import CategoryConversionChart from '../../components/CategoryConversionChart'
import ConversionTrendChart from '../../components/ConversionTrendChart'
import OverallPerformanceCard from '../../components/OverallPerformanceCard'
import CampaignAnalyticsTable from '../../components/CampaignAnalyticsTable'

function MessagingAnalyticsPage() {
	const today = new Date()
	const ninetyDaysAgo = subDays(today, 90)

	const [queryState] = useQueryStates(
		{
			startDate: parseAsString.withDefault(format(ninetyDaysAgo, 'yyyy-MM-dd')),
			endDate: parseAsString.withDefault(format(today, 'yyyy-MM-dd')),
			messageType: parseAsString.withDefault(''),
			period: parseAsString.withDefault('90'),
		},
		{ shallow: false },
	)

	const [activeTab, setActiveTab] = useState<'combined' | 'sms' | 'line'>(
		'combined',
	)

	// Fetch analytics data (no channel filter - we show all and filter by tabs)
	const { data, isLoading } = useQueryGetMessagingAnalytics({
		startDate: queryState.startDate || undefined,
		endDate: queryState.endDate || undefined,
		messageType: (queryState.messageType || undefined) as
			| PathsApiAdminMessageAnalyticsGetParametersQueryMessageType
			| undefined,
	})

	return (
		<PageContainer>
			<div className="space-y-6">
				{/* Page Header */}
				<div>
					<h1 className="text-2xl font-bold tracking-tight">
						Messaging Analytics
					</h1>
					<p className="text-muted-foreground">
						วิเคราะห์ประสิทธิภาพการส่งข้อความและ Reactivation Conversion
					</p>
				</div>

				{/* Filters */}
				<Card>
					<CardContent className="pt-6">
						<MessagingAnalyticsFilters />
					</CardContent>
				</Card>

				{/* Channel Tabs */}
				<Tabs
					value={activeTab}
					onValueChange={(v) => setActiveTab(v as 'combined' | 'sms' | 'line')}
				>
					<TabsList>
						<TabsTrigger value="combined">รวมทุกช่องทาง</TabsTrigger>
						<TabsTrigger value="sms">SMS</TabsTrigger>
						<TabsTrigger value="line">LINE</TabsTrigger>
					</TabsList>

					<TabsContent value="combined" className="mt-6 space-y-6">
						{/* Overview KPI Cards */}
						<ReactivationOverviewCards
							analytics={data?.overall}
							isLoading={isLoading}
						/>

						{/* Category Conversion Table */}
						<CategoryConversionTable
							metrics={data?.overall?.depositConversionByCategory}
							isLoading={isLoading}
						/>

						{/* Charts Row */}
						<div className="grid gap-6 lg:grid-cols-2">
							<CategoryConversionChart
								metrics={data?.overall?.depositConversionByCategory}
								isLoading={isLoading}
							/>
							<ConversionTrendChart
								metrics={data?.overall?.depositConversionByCategory}
								isLoading={isLoading}
							/>
						</div>

						{/* Overall Performance */}
						<OverallPerformanceCard
							analytics={data?.overall}
							title="ประสิทธิภาพรวม (SMS + LINE)"
							isLoading={isLoading}
						/>

						{/* Campaign Table */}
						<CampaignAnalyticsTable
							campaigns={[
								...(data?.smsCampaigns || []),
								...(data?.lineCampaigns || []),
							]}
							isLoading={isLoading}
							title="แคมเปญทั้งหมด"
						/>
					</TabsContent>

					<TabsContent value="sms" className="mt-6 space-y-6">
						{/* Overview KPI Cards */}
						<ReactivationOverviewCards
							analytics={data?.smsOverall}
							isLoading={isLoading}
						/>

						{/* Category Conversion Table */}
						<CategoryConversionTable
							metrics={data?.smsOverall?.depositConversionByCategory}
							isLoading={isLoading}
						/>

						{/* Charts Row */}
						<div className="grid gap-6 lg:grid-cols-2">
							<CategoryConversionChart
								metrics={data?.smsOverall?.depositConversionByCategory}
								isLoading={isLoading}
							/>
							<ConversionTrendChart
								metrics={data?.smsOverall?.depositConversionByCategory}
								isLoading={isLoading}
							/>
						</div>

						{/* Overall Performance */}
						<OverallPerformanceCard
							analytics={data?.smsOverall}
							title="ประสิทธิภาพ SMS"
							isLoading={isLoading}
						/>

						{/* Campaign Table */}
						<CampaignAnalyticsTable
							campaigns={data?.smsCampaigns || []}
							isLoading={isLoading}
							title="แคมเปญ SMS"
						/>
					</TabsContent>

					<TabsContent value="line" className="mt-6 space-y-6">
						{/* Overview KPI Cards */}
						<ReactivationOverviewCards
							analytics={data?.lineOverall}
							isLoading={isLoading}
						/>

						{/* Category Conversion Table */}
						<CategoryConversionTable
							metrics={data?.lineOverall?.depositConversionByCategory}
							isLoading={isLoading}
						/>

						{/* Charts Row */}
						<div className="grid gap-6 lg:grid-cols-2">
							<CategoryConversionChart
								metrics={data?.lineOverall?.depositConversionByCategory}
								isLoading={isLoading}
							/>
							<ConversionTrendChart
								metrics={data?.lineOverall?.depositConversionByCategory}
								isLoading={isLoading}
							/>
						</div>

						{/* Overall Performance */}
						<OverallPerformanceCard
							analytics={data?.lineOverall}
							title="ประสิทธิภาพ LINE"
							isLoading={isLoading}
						/>

						{/* Campaign Table */}
						<CampaignAnalyticsTable
							campaigns={data?.lineCampaigns || []}
							isLoading={isLoading}
							title="แคมเปญ LINE"
						/>
					</TabsContent>
				</Tabs>
			</div>
		</PageContainer>
	)
}

const GuardedMessagingAnalyticsPage = withUserPermission(
	MessagingAnalyticsPage,
	{
		permissionType: PermissionType.Read,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedMessagingAnalyticsPage as MessagingAnalyticsPage }
